import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, catchError, EMPTY, finalize, Observable, of } from 'rxjs';

/** Models */
import { ChangePasswordModel, UserConfigurationModel, UserModel } from '../models/user-model';
import { AuthenticateRequestModel } from '../models/request/authenticate-request-model';
import { AuthenticateResponseModel } from '../models/response/authenticate-response-model';

/** Environment */
import { environment } from '../../environments/environment';
import { UserSearchRequestModel } from '../models/request/user-search-request-model';
import { PagedList } from '../models/common/paged-list';
import { UriResponse } from '../models/common/uri-response';
import { UserCreateRequestModelNew } from '../models/request/user-create-request-model';
import { UserUpdateRequestModel } from '../models/request/user-update-request-model';
import { PasswordChangeOTPRequestModel } from '../models/request/password-change-otp.model';
import { RoleModel } from '../models/role-model';
import { StoreRoleModel } from '../models/store-role-model';
import { SearchRequest, SearchResponse, Url } from '../models/request/user-search-model';
import { ToasterService } from '../utils/toaster.service';
import { SKIP_AUTH_HEADER, SUPPRESS_ERROR_TOAST_HEADER } from '../utils/api-error.utils';


@Injectable({
  providedIn: 'root'
})
export class UserService {
  private searchIsLoading = new BehaviorSubject<boolean>(false);
  isLoading$: Observable<boolean> = this.searchIsLoading.asObservable();

  private ctrlUrl: string = `${environment.apiUrl}/v1/user`;
  private ctrlUrl1: string = `${environment.apiUrl}/v1`;

  constructor(private http: HttpClient, private toasterSvc: ToasterService) {
  }

  public getMyPendingActionAsync(): Observable<any> {
    return this.http.get<any>(`${this.ctrlUrl}/my/pending/action`);
  }

  public authenticateAsync(authenticateRequestModel: AuthenticateRequestModel): Observable<AuthenticateResponseModel> {
    return this.http.post<AuthenticateResponseModel>(`${this.ctrlUrl}/authenticate`, authenticateRequestModel);
  }

  public getMyAsync(): Observable<UserModel> {
    return this.http.get<UserModel>(`${this.ctrlUrl}/my`);
  }

  public updateMyConfigurationAsync(
    payload: Pick<UserConfigurationModel, 'type' | 'key' | 'value'>
  ): Observable<UserConfigurationModel> {
    return this.http.put<UserConfigurationModel>(
      `${this.ctrlUrl}/my/configuration`,
      payload
    );
  }

  public deleteUserAsync(user: any) {
    return this.http.delete(`${this.ctrlUrl}/DeleteUserAsync/${user}`);
  }

  public searchAsync(userSearchRequestModel: UserSearchRequestModel): Observable<PagedList<UserModel>> {
    return this.http.post<PagedList<UserModel>>(`${this.ctrlUrl}/search`, userSearchRequestModel);
  }

  searchUserAsync<T>(
    payload: SearchRequest,
    url: Url
  ): Observable<SearchResponse<T>> {
    this.searchIsLoading.next(true);

    return this.http
      .post<SearchResponse<T>>(
        `${environment.apiUrl}/v1/${url}/search`,
        payload
      )
      .pipe(
        catchError((error) => {
          console.error('Search Error:', error);
          return of(null);
        }),
        finalize(() => {
          this.searchIsLoading.next(false);
        })
      );
  }

  public uploadProfilePictureAsync(formData: FormData): Observable<UriResponse> {
    return this.http.post<UriResponse>(`${this.ctrlUrl}/upload/profile-picture`, formData);
  }

  /** @param companyId creates the user in this organization instead of the selected one. */
  public createAsync(userCreateRequestModel: UserCreateRequestModelNew, companyId?: string): Observable<UserModel> {
    const options = companyId ? { headers: new HttpHeaders({ Company: companyId }) } : {};
    return this.http.post<UserModel>(`${this.ctrlUrl}`, userCreateRequestModel, options);
  }
  public verifyInviteUrlAsync(inviteId: string): Observable<any> {
    // Opened straight from an emailed invite link with no logged-in session — a
    // stale JWT left over from another user's session on this browser must not
    // be attached to this anonymous request.
    return this.http.get<any>(`${this.ctrlUrl1}/vendor/verify/url/${inviteId}`, {
      headers: new HttpHeaders({ [SKIP_AUTH_HEADER]: 'true' }),
    });
  }
  // createAsync(
  //   userCreateRequestModel: UserCreateRequestModel
  // ): Observable<UserModel> {
  //   return this.http
  //     .post<UserModel>(`${this.ctrlUrl}`, userCreateRequestModel)
  //     .pipe(
  //       tap(() => {
  //         this.toasterSvc.showSuccess(
  //           'Successfully created!'
  //         );
  //       }),
  //       catchError((error) => {
  //         // this.toasterSvc.showError(
  //         //   error?.error?.message || 'Something went wrong. Please try again later.'
  //         // );
  //         return EMPTY;
  //       })
  //     );
  // }
  // updateAsync(
  //   userUpdateRequestModel: UserUpdateRequestModel
  // ): Observable<void> {
  //   return this.http
  //     .put<void>(`${this.ctrlUrl}`, userUpdateRequestModel)
  //     .pipe(
  //       tap(() => {
  //         this.toasterSvc.showSuccess(
  //           'Successfully updated!'
  //         );
  //       }),
  //       catchError((error) => {
  //         // this.toasterSvc.showError(
  //         //   error?.error?.message || 'Something went wrong. Please try again later.'
  //         // );
  //         return EMPTY;
  //       })
  //     );
  // }

  public updateAsync(userUpdateRequestModel: UserUpdateRequestModel): Observable<void> {
    return this.http.put<void>(`${this.ctrlUrl}`, userUpdateRequestModel);
  }

  public changePasswordAsync(changePasswordModel: ChangePasswordModel): Observable<void> {
    return this.http.post<void>(`${this.ctrlUrl}/password/update`, changePasswordModel);
  }

  public requestOtpAsync(type: string, email: string): Observable<void> {
    return this.http.post<void>(`${this.ctrlUrl}/otp/request`, { type, email });
  }

  public verifyOtpAsync(code: string): Observable<void> {
    return this.http.post<void>(`${this.ctrlUrl}/otp/verify`, { code });
  }

  public getChangeEmailOTP(newEmail: string): Observable<void> {
    return this.http.post<void>(`${this.ctrlUrl}/email/update`, { newEmail });
  }

  public VerifyChangeEmailOTP(otp: string): Observable<void> {
    return this.http.post<void>(`${this.ctrlUrl}/email/update/verify`, { otp });
  }

  public getChangePasswordOTP(email: string, suppressErrorToast = false): Observable<void> {
    let headers = new HttpHeaders();
    if (suppressErrorToast) {
      headers = headers.set(SUPPRESS_ERROR_TOAST_HEADER, 'true');
    }
    return this.http.post<void>(`${this.ctrlUrl}/password/reset`, { email }, { headers });
  }

  public verifyChangePasswordOTPOnly(otp: string, suppressErrorToast = false): Observable<void> {
    let headers = new HttpHeaders();
    if (suppressErrorToast) {
      headers = headers.set(SUPPRESS_ERROR_TOAST_HEADER, 'true');
    }
    return this.http.post<void>(`${this.ctrlUrl}/password/reset/verify`, { otp }, { headers });
  }

  public VerifyChangePasswordOTP(passwordChangeOTPRequest: PasswordChangeOTPRequestModel, suppressErrorToast = false): Observable<void> {
    let headers = new HttpHeaders();
    if (suppressErrorToast) {
      headers = headers.set(SUPPRESS_ERROR_TOAST_HEADER, 'true');
    }
    return this.http.post<void>(`${this.ctrlUrl}/password/reset/verify`, passwordChangeOTPRequest, { headers });
  }

  public checkDuplicateRole(companyRoleid: any): Observable<any> {
    return this.http.post<any>(`${this.ctrlUrl}/check/companyrole`, companyRoleid);
  }

  public checkStoreRole(paylaod: any): Observable<any> {
    return this.http.post<any>(`${this.ctrlUrl}/check/storerole`, paylaod);
  }

  public getCompanyRolesAsync(storeId: any): Observable<any> {
    return this.http.get<void>(`${this.ctrlUrl}/search/companyroles/${storeId}`);
  }

  /** GET /v1/user/approvers/{type} — e.g. AccountsPayable */
  public getApproversAsync(type: string): Observable<UserModel[]> {
    return this.http.get<UserModel[]>(`${this.ctrlUrl}/approvers/${type}`);
  }

}
